'use client';

import { useCallback, useRef, useState } from 'react';
import {
  FileImage,
  FileText,
  Search,
  ZoomIn,
  ZoomOut,
  RotateCw,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Maximize2,
} from 'lucide-react';
import PdfThumbnail from '@/components/shared/PdfThumbnail';

export interface DocumentPreviewItem {
  url: string;
  fileType: string | null;
  fileName: string;
}

interface DocumentImageViewerProps {
  items: DocumentPreviewItem[];
  isDark: boolean;
  onOpenFullViewer?: (index: number) => void;
}

export function DocumentImageViewer({
  items,
  isDark,
  onOpenFullViewer,
}: DocumentImageViewerProps) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const isPdf = useCallback(
    (idx: number) => {
      const ft = items[idx]?.fileType;
      return ft === 'pdf' || /\.pdf($|\?)/i.test(items[idx]?.url || '');
    },
    [items],
  );

  // Reset zoom/rotation when switching pages (render-time adjustment)
  const prevIdxRef = useRef(activeIdx);
  if (prevIdxRef.current !== activeIdx) {
    prevIdxRef.current = activeIdx;
    setZoom(1);
    setRotation(0);
  }

  // Clamp active index during render
  const clampedIdx = items.length > 0 ? Math.min(activeIdx, items.length - 1) : 0;
  if (items.length > 0 && activeIdx !== clampedIdx) {
    setActiveIdx(clampedIdx);
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <FileImage className="h-12 w-12 text-gray-400" />
      </div>
    );
  }

  const currentItem = items[clampedIdx];
  if (!currentItem) return null;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Toolbar */}
      <div
        className={`flex items-center gap-1 border-b px-2 py-1.5 flex-shrink-0 ${
          isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'
        }`}
      >
        {/* Page tabs (if multiple) */}
        {items.length > 1 && (
          <div className="flex items-center gap-0.5 overflow-x-auto">
            {items.map((item, idx) => (
              <button
                key={`tab-${idx}`}
                type="button"
                onClick={() => setActiveIdx(idx)}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                  clampedIdx === idx
                    ? isDark
                      ? 'bg-gray-800 text-gray-100'
                      : 'bg-gray-100 text-gray-900'
                    : isDark
                      ? 'text-gray-400 hover:bg-gray-800'
                      : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                {isPdf(idx) ? (
                  <FileText className="w-3 h-3" />
                ) : (
                  <FileImage className="w-3 h-3" />
                )}
                {item.fileName || `Page ${idx + 1}`}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1" />

        {/* Zoom controls */}
        <button
          type="button"
          onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
          disabled={zoom <= 0.5}
          className={`rounded p-1 transition-colors disabled:opacity-40 ${
            isDark
              ? 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
              : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
          }`}
          title="Zoom out"
        >
          <ZoomOut className="h-4 w-4" />
        </button>
        <span className={`text-xs font-mono tabular-nums ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {Math.round(zoom * 100)}%
        </span>
        <button
          type="button"
          onClick={() => setZoom((z) => Math.min(4, z + 0.25))}
          disabled={zoom >= 4}
          className={`rounded p-1 transition-colors disabled:opacity-40 ${
            isDark
              ? 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
              : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
          }`}
          title="Zoom in"
        >
          <ZoomIn className="h-4 w-4" />
        </button>

        {/* Rotation controls */}
        <div className={`mx-1 h-4 w-px ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
        <button
          type="button"
          onClick={() => setRotation((r) => (r - 90) % 360)}
          className={`rounded p-1 transition-colors ${
            isDark
              ? 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
              : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
          }`}
          title="Rotate left"
        >
          <RotateCcw className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => setRotation((r) => (r + 90) % 360)}
          className={`rounded p-1 transition-colors ${
            isDark
              ? 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
              : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
          }`}
          title="Rotate right"
        >
          <RotateCw className="h-4 w-4" />
        </button>

        <div className={`mx-1 h-4 w-px ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />

        {/* Page navigation */}
        {items.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => setActiveIdx((i) => Math.max(0, i - 1))}
              disabled={clampedIdx === 0}
              className={`rounded p-1 transition-colors disabled:opacity-40 ${
                isDark
                  ? 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
              title="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className={`text-xs tabular-nums ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {clampedIdx + 1}/{items.length}
            </span>
            <button
              type="button"
              onClick={() => setActiveIdx((i) => Math.min(items.length - 1, i + 1))}
              disabled={clampedIdx === items.length - 1}
              className={`rounded p-1 transition-colors disabled:opacity-40 ${
                isDark
                  ? 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
              title="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </>
        )}

        {/* Full viewer button */}
        {onOpenFullViewer && (
          <button
            type="button"
            onClick={() => onOpenFullViewer(clampedIdx)}
            className={`rounded p-1 transition-colors ${
              isDark
                ? 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
            }`}
            title="Open in full viewer"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Document display area */}
      <div
        ref={containerRef}
        className="flex flex-1 items-center justify-center overflow-auto bg-gray-100 dark:bg-gray-950 relative"
      >
        {isPdf(clampedIdx) ? (
          <div
            style={{
              transform: `scale(${zoom}) rotate(${rotation}deg)`,
              transformOrigin: 'center center',
              transition: 'transform 0.15s ease-out',
            }}
          >
            <PdfThumbnail
              url={currentItem.url}
              alt={currentItem.fileName || `Document ${clampedIdx + 1}`}
              className="max-h-full max-w-full object-contain"
              fallbackIcon={<FileImage className="h-12 w-12 text-gray-400" />}
            />
          </div>
        ) : (
          <img
            src={currentItem.url}
            alt={currentItem.fileName || `Document ${clampedIdx + 1}`}
            className="max-h-full max-w-full object-contain cursor-pointer"
            style={{
              transform: `scale(${zoom}) rotate(${rotation}deg)`,
              transformOrigin: 'center center',
              transition: 'transform 0.15s ease-out',
            }}
            onClick={() => onOpenFullViewer?.(clampedIdx)}
            onError={(e) => {
              const target = e.currentTarget;
              target.style.display = 'none';
              const parent = target.parentElement;
              if (parent && !parent.querySelector('.fallback-icon')) {
                const fallback = document.createElement('div');
                fallback.className = 'fallback-icon flex flex-col items-center gap-2';
                fallback.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>';
                parent.appendChild(fallback);
              }
            }}
          />
        )}
      </div>
    </div>
  );
}
